import React, { useMemo, useState } from 'react';
import { ENGINE_OPTIONS, canUseEngine, engineLabel, type Engine } from 'api/analysisApi';
import { useAuthStore } from 'store/useAuthStore';
import { useBacktestSettings } from 'hooks/useBacktestSettings';
import { useForkCompare } from 'hooks/useFork';
import { fmtPct, fmtR } from 'components/history/backtestFormat';
import type { ForkCompareRow } from 'types/history';
import './compare.css';

/** Engines that can appear as a fork target. */
const COMPARABLE: Engine[] = ['brainer_v1', 'brainer_v1_nohm', 'enforcer', 'enforcer_v3', 'petar_v4', 'stojan_v5', 'claude'];

interface Metric {
	key: string;
	label: string;
	title: string;
	value: (r: ForkCompareRow) => string;
	/** Which direction is better — drives the winner highlight. Omitted for
	 *  counts, where "more" is neither good nor bad. */
	dir?: 'up' | 'down';
	raw?: (r: ForkCompareRow) => number | null;
}

const METRICS: Metric[] = [
	{
		key: 'win_rate',
		label: 'Win rate',
		title: 'Targets hit vs stops hit, excluding time-stopped trades',
		value: (r) => fmtPct(r.aggregate.win_rate),
		raw: (r) => r.aggregate.win_rate,
		dir: 'up',
	},
	{
		key: 'expectancy_R',
		label: 'Expectancy',
		title: 'Average R per closed trade — the number that actually compounds',
		value: (r) => fmtR(r.aggregate.expectancy_R),
		raw: (r) => r.aggregate.expectancy_R,
		dir: 'up',
	},
	{
		key: 'avg_realized_R',
		label: 'Avg R',
		title: 'Mean realized R across closed trades',
		value: (r) => fmtR(r.aggregate.avg_realized_R),
		raw: (r) => r.aggregate.avg_realized_R,
		dir: 'up',
	},
	{
		key: 'fill_rate',
		label: 'Fill rate',
		title: 'Share of proposals whose entry zone was actually reached in time',
		value: (r) => fmtPct(r.aggregate.fill_rate),
		raw: (r) => r.aggregate.fill_rate,
		dir: 'up',
	},
	{
		key: 'no_entry_rate',
		label: 'No entry',
		title: 'Proposals price never came back to — a cost, not a neutral outcome',
		value: (r) => fmtPct(r.aggregate.no_entry_rate),
		raw: (r) => r.aggregate.no_entry_rate,
		dir: 'down',
	},
	{
		key: 'count_proposals_actionable',
		label: 'Trades',
		title: 'Actionable proposals the engine produced over this set',
		value: (r) => String(r.aggregate.count_proposals_actionable ?? 0),
	},
	{
		key: 'count_wait_skipped',
		label: 'Waits',
		title: 'Setups the engine declined to trade at all',
		value: (r) => String(r.aggregate.count_wait_skipped ?? 0),
	},
	{
		key: 'agreement',
		label: 'Agrees with parent',
		title:
			'Share of runs whose direction matched the original run on the same input. Agreement is not accuracy — a low number can mean a contrarian engine or simply a noisy one.',
		value: (r) => (r.is_baseline ? '—' : fmtPct(r.direction_agreement)),
	},
];

/**
 * Head-to-head engine comparison over one set of inputs.
 *
 * Everything here rests on the parent intersection the server computes: only
 * analyses that *every* compared engine was forked from are counted, so no engine
 * is flattered by having been run on an easier subset. Sample size sits next to
 * the numbers rather than in a footnote, because at these run counts it is
 * usually the most important number on the screen.
 */
export function CompareView() {
	const settings = useBacktestSettings();
	// Read straight off the saved settings rather than sharing History's rule-set
	// state: this view has no table to keep in step with, and the saved rules are
	// the ones the stored sweeps were computed under.
	const pair = {
		window: settings.settings?.entry_window ?? 6,
		closeWindow: settings.settings?.close_window ?? 0,
	};
	const user = useAuthStore((s) => s.user);
	const comparable = COMPARABLE.filter((e) => canUseEngine(user, e));
	const [engines, setEngines] = useState<Engine[]>(() =>
		(['enforcer', 'brainer_v1'] as Engine[]).filter((e) => canUseEngine(useAuthStore.getState().user, e)),
	);
	const [asset, setAsset] = useState('');
	const [timeframe, setTimeframe] = useState('');

	const params = useMemo(
		() =>
			engines.length
				? {
						engines: [...engines] as string[],
						window: pair.window,
						closeWindow: pair.closeWindow,
						asset: asset.trim().toUpperCase() || undefined,
						timeframe: timeframe.trim() || undefined,
						includeBaseline: true,
					}
				: null,
		// Depend on the values, not the object — `pair` is rebuilt every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[engines, pair.window, pair.closeWindow, asset, timeframe],
	);

	const { data, loading, error } = useForkCompare(params);

	const toggle = (e: Engine) =>
		setEngines((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));

	/** Best value per metric, so the leader can be marked. The baseline is
	 *  excluded — it is the control, not a competitor. */
	const best = useMemo(() => {
		const out: Record<string, number | null> = {};
		if (!data) return out;
		const contenders = data.rows.filter((r) => !r.is_baseline);
		for (const m of METRICS) {
			if (!m.raw || !m.dir) continue;
			const vals = contenders
				.map((r) => m.raw!(r))
				.filter((v): v is number => v != null);
			out[m.key] = vals.length ? (m.dir === 'up' ? Math.max(...vals) : Math.min(...vals)) : null;
		}
		return out;
	}, [data]);

	const contenderCount = data ? data.rows.filter((r) => !r.is_baseline).length : 0;

	return (
		<div className='cmp-view'>
			<div className='cmp-head'>
				<h2 className='cmp-title'>Engine comparison</h2>
				<p className='cmp-sub'>
					Each engine's forks are scored against the same parent analyses — same input
					data, same moment in market history — so the difference between columns is the
					engine and nothing else. Fork some analyses from History first; engines with no
					parents in common have nothing to compare.
				</p>
			</div>

			<div className='cmp-controls'>
				<div className='cmp-engines'>
					{comparable.map((e) => (
						<button
							key={e}
							className={`cmp-chip${engines.includes(e) ? ' active' : ''}`}
							onClick={() => toggle(e)}
							title={engineLabel(e)}
						>
							{ENGINE_OPTIONS.find((o) => o.value === e)?.badge ?? e}
						</button>
					))}
				</div>
				<input
					className='cmp-input'
					placeholder='All symbols'
					value={asset}
					onChange={(ev) => setAsset(ev.target.value)}
					aria-label='Filter by symbol'
				/>
				<input
					className='cmp-input'
					placeholder='All timeframes'
					value={timeframe}
					onChange={(ev) => setTimeframe(ev.target.value)}
					aria-label='Filter by timeframe'
				/>
				<span className='cmp-pair' title='The backtest rules these results were scored under'>
					entry {pair.window} / close {pair.closeWindow || 'none'}
				</span>
			</div>

			{error && <div className='cmp-error'>{error}</div>}
			{loading && <div className='cmp-empty'>Loading…</div>}

			{!loading && !error && data && data.n_parents === 0 && (
				<div className='cmp-empty'>
					{engines.length === 0
						? 'Pick at least one engine.'
						: 'No analysis has been forked onto every selected engine yet. Fork the same set onto each from History → “Fork these…”, then run the backtest sweep so the forks are scored.'}
				</div>
			)}

			{!loading && !error && data && data.n_parents > 0 && (
				<>
					<div className='cmp-scope'>
						Comparing over <strong>{data.n_parents}</strong> shared parent
						{data.n_parents === 1 ? '' : ' analyses'}
						{data.n_parents < 20 && (
							<span className='cmp-thin'>
								{' '}
								— a thin sample. v1 can flip direction run-to-run on identical input, so
								treat a gap this small as a hint, not a result.
							</span>
						)}
					</div>

					<div className='cmp-table-wrap'>
						<table className='cmp-table'>
							<thead>
								<tr>
									<th className='cmp-metric-col'>Metric</th>
									{data.rows.map((r) => (
										<th key={r.engine} className={r.is_baseline ? 'cmp-baseline' : ''}>
											<div className='cmp-col-engine'>
												{r.is_baseline ? 'Original runs' : engineLabel(r.engine as Engine)}
											</div>
											<div className='cmp-col-n'>
												{r.n_runs} run{r.n_runs === 1 ? '' : 's'}
												{r.n_runs !== r.n_parents && ` · ${r.n_parents} parents`}
											</div>
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{METRICS.map((m) => (
									<tr key={m.key}>
										<th className='cmp-metric-col' title={m.title}>
											{m.label}
										</th>
										{data.rows.map((r) => {
											const raw = m.raw?.(r) ?? null;
											const isBest =
												!r.is_baseline &&
												raw != null &&
												best[m.key] != null &&
												raw === best[m.key] &&
												contenderCount > 1;
											return (
												<td
													key={r.engine}
													className={[
														r.is_baseline ? 'cmp-baseline' : '',
														isBest ? 'cmp-best' : '',
													]
														.filter(Boolean)
														.join(' ')}
												>
													{m.value(r)}
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<p className='cmp-foot'>
						Metrics come from the same aggregate the History summary bar renders, so these
						numbers are directly comparable to the production ones. A highlighted cell is
						the best of the forked engines on that row — not evidence that the difference
						is real.
					</p>
				</>
			)}
		</div>
	);
}
